import React, { useEffect, useRef, useState } from 'react';
import { Box, Fade, useMediaQuery, useTheme } from '@material-ui/core';
import { wait } from 'globals/utils';
import { registerEmploymentDetailsStep as registerLocales } from 'globals/locales/registration';
import defaultLocales from 'globals/locales/default';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormFullWidthContainer from 'ui-elements/form-elements/FsFormFullWidthContainer';
import FsNextArrowIcon from 'ui-elements/icons/FsNextArrow';
import FsSentenceSelect from 'ui-elements/form-elements/FsSentenceSelect';
import {
  numberOfEmployeesOptionList,
  numberOfCustomersOptionList,
  industriesList,
  departmentsList,
} from 'globals/locales/select-options';
import FsButton from 'ui-elements/buttons/FsButton';
import {
  SelectWrapper,
  Sentence,
} from './FsRegisterEmploymentDetailsForm.style-elements';
import { skipButtonStyle } from '../FsInviteNewUserRepetitiveForm/FsInviteNewUserRepetitiveForm.style';

export interface FsRegisterEmploymentDetailsFormDataProps {
  industry: string;
  department: string;
  numberOfEmployees: string;
  numberOfCustomers: string;
}

interface FsRegisterEmploymentDetailsFormProps
  extends FsRegisterEmploymentDetailsFormDataProps {
  onClose?: () => void;
  onGoNext?: (props?: FsRegisterEmploymentDetailsFormDataProps) => void;
}

const FsRegisterEmploymentDetailsForm: React.FC<FsRegisterEmploymentDetailsFormProps> = ({
  onClose,
  onGoNext,
  ...defaults
}) => {
  const formEl = useRef<HTMLFormElement>(null);
  const [industry, setIndustry] = useState<string>(defaults.industry);
  const [department, setDepartment] = useState<string>(defaults.department);
  const [numberOfEmployees, setNumberOfEmployees] = useState<string>(
    defaults.numberOfEmployees
  );
  const [numberOfCustomers, setNumberOfCustomers] = useState<string>(
    defaults.numberOfCustomers
  );
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const theme = useTheme();
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const fields = [
    {
      isSelect: true,
      options: industriesList,
      required: true,
      name: defaultLocales.industry.toLowerCase(),
      value: industry,
      label: defaultLocales.chooseIndustry.toLowerCase(),
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setIndustry(e.target?.value);
      },
      prefix: registerLocales.industryPrefix,
      suffix: registerLocales.industrySuffix,
      isBlocker: false,
      autoFocus: true,
    },
    {
      isSelect: true,
      options: departmentsList,
      required: true,
      name: 'department',
      value: department,
      label: defaultLocales.chooseDepartment.toLowerCase(),
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setDepartment(e.target?.value);
      },
      prefix: registerLocales.positionPrefix,
      suffix: registerLocales.positionSuffix,
      isBlocker: !industry,
    },
    {
      isSelect: true,
      options: numberOfEmployeesOptionList,
      required: true,
      name: 'employees-number',
      value: numberOfEmployees,
      label: defaultLocales.chooseNumber.toLowerCase(),
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setNumberOfEmployees(e.target?.value);
      },
      prefix: registerLocales.numberOfEmployeesPrefix,
      suffix: registerLocales.numberOfEmployeesSuffix,
      isBlocker: !department,
    },
    {
      isSelect: true,
      options: numberOfCustomersOptionList,
      required: true,
      name: 'customer-number',
      value: numberOfCustomers,
      label: defaultLocales.chooseNumber.toLowerCase(),
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setNumberOfCustomers(e.target?.value);
      },
      prefix: registerLocales.numberOfCustomersPrefix,
      suffix: registerLocales.numberOfCustomersSuffix,
      isBlocker: !numberOfEmployees,
    },
  ];

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('Save changes', {
      numberOfEmployees,
      numberOfCustomers,
    });
    await wait(1000);
    setPending(false);
    if (onClose) onClose();
    if (onGoNext)
      onGoNext({
        industry,
        department,
        numberOfEmployees,
        numberOfCustomers,
      });
  };

  const handleSkip = () => {
    if (onGoNext) onGoNext();
  };

  useEffect(() => {
    const isDefaultFormValid = !!formEl?.current?.checkValidity();
    setIsFormValid(isDefaultFormValid);
  }, [industry, department, numberOfEmployees, numberOfCustomers]);

  return (
    <Box>
      <form onSubmit={handleSubmit} ref={formEl}>
        {fields.map(
          ({
            name,
            isSelect,
            prefix,
            suffix,
            options,
            isBlocker,
            ...otherProps
          }) => (
            <FsFormFullWidthContainer key={name}>
              {isSelect && options && (
                <Fade in={!isBlocker} timeout={300}>
                  <Sentence component="div">
                    {prefix && `${prefix} `}
                    <SelectWrapper>
                      <FsSentenceSelect
                        {...{ ...otherProps, name, options }}
                        optionCase="lc"
                      />
                    </SelectWrapper>
                    {suffix && ` ${suffix}`}
                  </Sentence>
                </Fade>
              )}
            </FsFormFullWidthContainer>
          )
        )}
        <Box mt={10}>
          <FsPendingButton
            type="submit"
            variant="contained"
            color="primary"
            pending={pending}
            disabled={!isFormValid}
            endIcon={<FsNextArrowIcon />}
            width={160}
            spaceBetween
          >
            {defaultLocales.continue}
          </FsPendingButton>
          <Box ml={2} display="inline-block">
            <FsButton onClick={handleSkip} style={skipButtonStyle()}>
              {isUpSm ? defaultLocales.doItLater : defaultLocales.skip}
            </FsButton>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default FsRegisterEmploymentDetailsForm;
